"use client";

import { useMutation } from "@tanstack/react-query";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StarRating } from "@/features/feedback/components/star-rating";
import { useI18n } from "@/locales/client";
import { useTRPC } from "@/trpc/client";

interface FeedbackStepProps {
  reason: string;
  onBack: () => void;
  onSuccess: (feedbackText: string, rating?: number) => void;
}

export const FeedbackStep = ({
  reason,
  onBack,
  onSuccess,
}: FeedbackStepProps) => {
  const [body, setBody] = useState("");
  const [rating, setRating] = useState(0);
  const t = useI18n();
  const trpc = useTRPC();

  const submitFeedback = useMutation(
    trpc.cancellation.submitCancellationFeedback.mutationOptions({
      onSuccess: () => {
        onSuccess(body.trim(), rating > 0 ? rating : undefined);
      },
      onError: (error) => {
        toast.error(
          error.message || t("feedbackModal.feedbackForm.submitError"),
        );
      },
    }),
  );

  const handleSubmit = () => {
    submitFeedback.mutate({
      reason,
      outcome: "retained",
      feedbackText: body.trim() || undefined,
      rating: rating > 0 ? rating : undefined,
    });
  };

  const canSubmit = body.trim().length > 0 && !submitFeedback.isPending;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <label
          htmlFor="cancellation-feedback-body"
          className="mb-1.5 block text-[13px] font-medium text-foreground"
        >
          {t("cancellation.feedback.label")}{" "}
          <span className="text-red-500">*</span>
        </label>
        <Textarea
          id="cancellation-feedback-body"
          placeholder={t("cancellation.feedback.placeholder")}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="resize-vertical focus-visible:ring-brand-primary"
          rows={4}
        />
      </div>

      <div>
        <span className="mb-2 block text-[13px] font-medium text-foreground">
          {t("cancellation.feedback.ratingLabel")}
        </span>
        <StarRating value={rating} onChange={setRating} />
      </div>

      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="gap-1">
          <ChevronLeft className="size-4" />
          {t("cancellation.reason.goBack")}
        </Button>
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          {submitFeedback.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            t("cancellation.feedback.submit")
          )}
        </Button>
      </div>
    </div>
  );
};
